import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { authTenantServiceApi, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import BrandingPreview from "@/components/BrandingPreview";
import BrandingFields from "@/components/BrandingFields";
import BrandingSummary from "@/components/BrandingSummary";
import ReadOnlyNotice from "@/components/ReadOnlyNotice";
import { defaultFeatureConfig, PLATFORM_DEFAULT } from "@/lib/tenant-theme";
import { Loader2, RotateCcw, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Tenant-admin branding & portal console. Thin client over
 * tis-auth-tenant-service's config endpoints (GET/PATCH
 * /tenants/:id/configuration) — the SINGLE source of truth both Talent
 * and Hub read. This page does NOT store branding anywhere else; a save
 * here is immediately reflected in the other portals the next time they
 * load their config. Writes `branding` (tenantBrandingSchema) and
 * `featureFlags` (modules/portals/labels/locale) in one PATCH.
 */
const TABS = [
  { id: "branding", label: "Branding" },
];

export default function AdminBranding() {
  const { user, isAdmin } = useAuth();
  const admin = isAdmin();
  const [branding, setBranding] = useState({ colors: {} });
  const [featureConfig, setFeatureConfig] = useState(defaultFeatureConfig());
  const [tab, setTab] = useState("branding");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const tenantId = user?.tenantId;

  useEffect(() => {
    if (!tenantId) return;
    const stagedKey = `staged_branding_${tenantId}`;
    const staged = localStorage.getItem(stagedKey);

    const applyConfig = (cfg) => {
      setBranding(cfg?.branding || { colors: {} });
      setFeatureConfig({ ...defaultFeatureConfig(), ...(cfg?.featureFlags || {}) });
    };

    const load = () =>
      authTenantServiceApi
        .get(`/api/v1/tenants/${tenantId}/configuration`)
        .then((res) => applyConfig(res.data))
        .catch((err) => { const m = errorMessage(err); if (m) setError(m); })
        .finally(() => setLoading(false));

    if (staged) {
      // Staged shape from the public wizard: { branding, featureConfig }.
      // Write BOTH branding and featureFlags to Auth config now that we
      // hold a real OWN/TSA token, then clear the staged entry.
      let parsed;
      try {
        parsed = JSON.parse(staged);
      } catch {
        localStorage.removeItem(stagedKey);
        load();
        return;
      }
      const stagedBranding = parsed.branding || parsed; // tolerate legacy shape
      const stagedFeatures = parsed.featureConfig || null;
      authTenantServiceApi
        .patch(`/api/v1/tenants/${tenantId}/configuration`, {
          branding: stagedBranding,
          ...(stagedFeatures ? { featureFlags: stagedFeatures } : {}),
        })
        .then(() => {
          localStorage.removeItem(stagedKey);
          setBranding(stagedBranding);
          if (stagedFeatures) setFeatureConfig({ ...defaultFeatureConfig(), ...stagedFeatures });
          setLoading(false);
        })
        .catch(() => load());
    } else {
      load();
    }
  }, [tenantId]);

  /**
   * Authenticated image upload: POST the picked file to the real
   * S3-backed route (POST /tenants/:id/configuration/branding-assets/:kind,
   * multipart, single `file` field). The route uploads to S3 AND writes
   * the returned public URL into branding[logoUrl|faviconUrl|
   * backgroundImageUrl] server-side, so BrandingFields stores that URL
   * (never a data: URL, which would fail the backend's logoUrl url()
   * validation and never reach S3). Returns the public URL on success.
   */
  const uploadImage = async (kind, file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await authTenantServiceApi.post(
      `/api/v1/tenants/${tenantId}/configuration/branding-assets/${kind}`,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data?.url;
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await authTenantServiceApi.patch(`/api/v1/tenants/${tenantId}/configuration`, {
        branding,
        featureFlags: featureConfig,
      });
      setSaved(true);
    } catch (err) {
      const m = errorMessage(err); if (m) setError(m);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = () => {
    setBranding({ colors: { ...PLATFORM_DEFAULT.colors }, fonts: { ...PLATFORM_DEFAULT.fonts } });
  };

  const includedPortals = Object.keys(featureConfig.portals || { talent: 1, hub: 1 });

  return (
    <Layout variant="tenant">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Branding & Portals</h1>
          <p className="mt-1 text-muted-foreground">Applied across your Hub and Talent portals.</p>
        </div>
        <div className="flex items-center gap-2">
          {admin && !editing && (
            <Button size="sm" className="gap-1.5" onClick={() => setEditing(true)} data-testid="branding-edit-btn">
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          )}
          {admin && (
            <Button variant="outline" size="sm" className="gap-1.5" onClick={resetToDefault} data-testid="branding-reset-btn">
              <RotateCcw className="h-4 w-4" /> Reset theme
            </Button>
          )}
        </div>
      </div>
      <ReadOnlyNotice what="branding" />

      {loading && <Loader2 className="mt-8 h-6 w-6 animate-spin text-primary" />}
      {error && <p className="mt-8 text-sm text-destructive" data-testid="branding-error">{error}</p>}

      {!loading && !editing && (
        <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_380px]">
          <Card>
            <CardHeader>
              <CardTitle>Current branding</CardTitle>
              <CardDescription>What your Talent and Hub portals use today. Click Edit to change it.</CardDescription>
            </CardHeader>
            <CardContent>
              <BrandingSummary branding={branding} featureConfig={featureConfig} portals={includedPortals} />
            </CardContent>
          </Card>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live preview</p>
            <BrandingPreview branding={branding} portals={includedPortals} featureConfig={featureConfig} />
          </div>
        </div>
      )}

      {!loading && editing && (
        <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_380px]">
          <div>
            <div className="inline-flex rounded-md border border-border bg-muted p-0.5" role="tablist">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  data-testid={`admin-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    "rounded px-3 py-1.5 text-sm font-medium transition-colors",
                    tab === t.id ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div className="mt-6">
              {tab === "branding" && (
                <Card>
                  <CardHeader>
                    <CardTitle>Brand identity</CardTitle>
                    <CardDescription>Logo, favicon, colors and typography — reused by Talent and Hub.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <BrandingFields branding={branding} onChange={setBranding} onError={setError} companyName={branding.shortName} onUploadImage={uploadImage} />
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button onClick={async () => { await save(); setEditing(false); }} disabled={saving} data-testid="branding-save-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save changes
              </Button>
              <Button variant="outline" onClick={() => setEditing(false)} data-testid="branding-cancel-btn">Cancel</Button>
              {saved && <p className="text-sm text-emerald-600" data-testid="branding-saved-message">Saved — applied across Talent & Hub.</p>}
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live preview</p>
            <BrandingPreview branding={branding} portals={includedPortals} featureConfig={featureConfig} />
          </div>
        </div>
      )}
    </Layout>
  );
}
